<template>
  <div class="expense-page">
    <div class="page-header">
      <div>
        <h1>Expense Track - Categories</h1>
        <p>Manage income and expense categories</p>
      </div>
      <button class="btn btn-primary" @click="openCreateModal">Create Category</button>
    </div>

    <div class="filters">
      <input v-model="filters.search" type="text" class="input" placeholder="Search by category name" @input="debouncedFetch" />
      <select v-model="filters.type" class="input" @change="fetchCategories">
        <option value="">All Types</option>
        <option value="INCOME">INCOME</option>
        <option value="EXPENSE">EXPENSE</option>
      </select>
      <select v-model="filters.status" class="input" @change="fetchCategories">
        <option value="">All Status</option>
        <option value="ACTIVE">ACTIVE</option>
        <option value="INACTIVE">INACTIVE</option>
      </select>
    </div>

    <div class="table-wrap" :class="{ 'table-loading': loading }">
      <div v-if="loading" class="table-loading-overlay">
        <div class="table-spinner">
          <div class="spinner-circle-small"></div>
          <span class="loading-text-small">Loading categories...</span>
        </div>
      </div>

      <table v-if="rows.length > 0" class="table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Type</th>
            <th>Status</th>
            <th>Default</th>
            <th>Updated On</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in paginatedRows" :key="row.categoryid || row.id">
            <td>{{ row.name || '--' }}</td>
            <td>{{ row.type || '--' }}</td>
            <td>{{ row.status || '--' }}</td>
            <td>{{ row.default ? 'Yes' : 'No' }}</td>
            <td>{{ formatDate(row.updatedat || row.updatedAt || row.createdat) }}</td>
            <td class="actions">
              <button class="mini" @click="openEditModal(row)">Edit</button>
              <button class="mini danger" @click="askDelete(row)">Delete</button>
            </td>
          </tr>
        </tbody>
      </table>

      <div v-else-if="!loading" class="empty-state">
        <img src="@/assets/guest-maint-staff.svg" alt="No categories illustration" class="empty-illustration-img" />
        <p>No categories found</p>
      </div>
    </div>

    <div v-if="totalPages > 1" class="pagination-wrapper">
      <button class="pagination-btn" @click="goToPage(currentPage - 1)" :disabled="currentPage === 1">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <polyline points="15 18 9 12 15 6"/>
        </svg>
      </button>
      <button v-for="page in visiblePages" :key="page" class="pagination-btn" :class="{ active: page === currentPage }" @click="goToPage(page)">
        {{ page }}
      </button>
      <button class="pagination-btn" @click="goToPage(currentPage + 1)" :disabled="currentPage === totalPages">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <polyline points="9 18 15 12 9 6"/>
        </svg>
      </button>
    </div>

    <div v-if="showFormModal" class="modal-overlay" @click.self="closeModal">
      <div class="modal">
        <h3>{{ modalMode === 'edit' ? 'Edit Category' : 'Create Category' }}</h3>
        <div class="form-grid">
          <label>Name</label>
          <input v-model="form.name" class="input" />
          <label>Type</label>
          <select v-model="form.type" class="input">
            <option value="">Select</option>
            <option value="INCOME">INCOME</option>
            <option value="EXPENSE">EXPENSE</option>
          </select>
          <label>Status</label>
          <select v-model="form.status" class="input">
            <option value="ACTIVE">ACTIVE</option>
            <option value="INACTIVE">INACTIVE</option>
          </select>
        </div>
        <div class="modal-actions">
          <button class="mini" @click="closeModal">Close</button>
          <button class="btn btn-primary" :disabled="saving" @click="saveCategory">{{ saving ? 'Saving...' : 'Save' }}</button>
        </div>
      </div>
    </div>

    <div v-if="showDeleteModal" class="modal-overlay" @click.self="showDeleteModal = false">
      <div class="modal">
        <h3>Delete Category</h3>
        <p>Are you sure you want to delete this category?</p>
        <div class="modal-actions">
          <button class="mini" @click="showDeleteModal = false">Cancel</button>
          <button class="mini danger" :disabled="saving" @click="deleteCategory">Delete</button>
        </div>
      </div>
    </div>

    <div v-if="toast.message" :class="['toast', toast.type]">{{ toast.message }}</div>
  </div>
</template>

<script>
import apiService from '@/services/api';
import { isNoDataError, isHmsSubscriptionInactiveError } from '@/utilities/errorHelper';

export default {
  name: 'ExpenseCategoriesView',
  data() {
    return {
      rows: [],
      loading: false,
      saving: false,
      filters: { search: '', type: '', status: '' },
      searchTimer: null,
      currentPage: 1,
      pageSize: 10,
      showFormModal: false,
      showDeleteModal: false,
      modalMode: 'create',
      selectedRow: null,
      form: { name: '', type: '', status: 'ACTIVE' },
      toast: { message: '', type: 'success' },
      requestSeq: 0,
    };
  },
  computed: {
    totalPages() {
      return Math.max(1, Math.ceil(this.rows.length / this.pageSize));
    },
    paginatedRows() {
      const start = (this.currentPage - 1) * this.pageSize;
      return this.rows.slice(start, start + this.pageSize);
    },
    objectId() {
      return this.$route.params.id || this.$route.query.objectid || '';
    },
    visiblePages() {
      const pages = [];
      const maxVisible = 4;
      let start = Math.max(1, this.currentPage - Math.floor(maxVisible / 2));
      let end = Math.min(this.totalPages, start + maxVisible - 1);
      if (end - start < maxVisible - 1) {
        start = Math.max(1, end - maxVisible + 1);
      }
      for (let i = start; i <= end; i += 1) pages.push(i);
      return pages;
    },
  },
  mounted() {
    this.fetchCategories();
  },
  methods: {
    formatDate(v) {
      if (!v) return '--';
      const d = new Date(v);
      if (Number.isNaN(d.getTime())) return String(v);
      return d.toLocaleString('en-NP', { year: 'numeric', month: 'short', day: 'numeric' });
    },
    showToast(message, type = 'success') {
      this.toast = { message, type };
      window.setTimeout(() => {
        if (this.toast.message === message) this.toast.message = '';
      }, 3000);
    },
    debouncedFetch() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        this.currentPage = 1;
        this.fetchCategories();
      }, 350);
    },
    async fetchCategories() {
      if (!this.objectId) return;
      this.loading = true;
      const seq = ++this.requestSeq;
      try {
        const response = await apiService.getExpenseCategories(this.objectId);
        if (seq !== this.requestSeq) return;
        const data = response?.data?.data;
        const list = Array.isArray(data) ? data : Array.isArray(response?.data) ? response.data : [];
        const search = this.filters.search.trim().toLowerCase();
        this.rows = list.filter((row) => {
          const matchType = !this.filters.type || row.type === this.filters.type;
          const matchStatus = !this.filters.status || row.status === this.filters.status;
          const matchSearch = !search || String(row.name || '').toLowerCase().includes(search);
          return matchType && matchStatus && matchSearch;
        });
        if (this.currentPage > this.totalPages) this.currentPage = this.totalPages;
      } catch (error) {
        if (isNoDataError(error)) {
          this.rows = [];
          return;
        }
        if (!isHmsSubscriptionInactiveError(error)) {
          this.showToast(error?.response?.data?.reason || 'Failed to load categories', 'error');
        }
      } finally {
        if (seq === this.requestSeq) this.loading = false;
      }
    },
    openCreateModal() {
      this.modalMode = 'create';
      this.form = { name: '', type: '', status: 'ACTIVE' };
      this.selectedRow = null;
      this.showFormModal = true;
    },
    openEditModal(row) {
      this.modalMode = 'edit';
      this.selectedRow = row;
      this.form = {
        name: row.name || '',
        type: row.type || '',
        status: row.status || 'ACTIVE',
      };
      this.showFormModal = true;
    },
    closeModal() {
      this.showFormModal = false;
    },
    goToPage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
      }
    },
    async saveCategory() {
      if (!this.form.name.trim() || !this.form.type) {
        this.showToast('Name and type are required', 'error');
        return;
      }
      this.saving = true;
      try {
        const payload = {
          name: this.form.name.trim(),
          type: this.form.type,
          status: this.form.status || 'ACTIVE',
        };
        if (this.modalMode === 'edit' && this.selectedRow) {
          await apiService.updateExpenseCategory(this.objectId, this.selectedRow.categoryid || this.selectedRow.id, payload);
          this.showToast('Category updated');
        } else {
          await apiService.createExpenseCategory(this.objectId, payload);
          this.showToast('Category created');
        }
        this.closeModal();
        await this.fetchCategories();
      } catch (error) {
        this.showToast(error?.response?.data?.reason || error?.response?.data?.message || 'Unable to save category', 'error');
      } finally {
        this.saving = false;
      }
    },
    askDelete(row) {
      this.selectedRow = row;
      this.showDeleteModal = true;
    },
    async deleteCategory() {
      if (!this.selectedRow) return;
      this.saving = true;
      try {
        await apiService.deleteExpenseCategory(this.objectId, this.selectedRow.categoryid || this.selectedRow.id);
        this.showToast('Category deleted');
        this.showDeleteModal = false;
        await this.fetchCategories();
      } catch (error) {
        this.showToast(error?.response?.data?.reason || 'Unable to delete category', 'error');
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.expense-page { padding: 20px; background: #f8fafc; min-height: 100%; }
.page-header { display: flex; justify-content: space-between; align-items: center; gap: 12px; margin-bottom: 14px; }
.page-header h1 { margin: 0; font-size: 20px; color: #1e293b; }
.page-header p { margin: 0; color: #64748b; font-size: 13px; }
.filters { display: grid; grid-template-columns: 1fr 180px 180px; gap: 10px; margin-bottom: 12px; }
.input { padding: 10px 12px; border: 1px solid #e2e8f0; border-radius: 8px; background: #fff; }
.btn { border: 0; border-radius: 8px; padding: 10px 14px; font-weight: 600; cursor: pointer; }
.btn-primary { background: linear-gradient(135deg, #5b2e8e, #7c3aed); color: #fff; }
.mini { border: 1px solid #e2e8f0; background: #fff; border-radius: 8px; padding: 7px 10px; cursor: pointer; }
.danger { color: #b91c1c; border-color: #fecaca; background: #fff5f5; }
.table-wrap { position: relative; background: #fff; border: 1px solid #e2e8f0; border-radius: 12px; overflow: auto; min-height: 220px; }
.table-wrap.table-loading { opacity: 0.85; }
.table { width: 100%; border-collapse: collapse; }
.table th, .table td { padding: 10px 12px; border-bottom: 1px solid #f1f5f9; text-align: left; font-size: 13px; }
.table th { background: #f1f5f9; color: #475569; font-weight: 600; }
.actions { display: flex; gap: 6px; }
.table-loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10;
}
.table-spinner { display: flex; align-items: center; gap: 10px; padding: 14px 18px; background: #fff; border-radius: 8px; box-shadow: 0 4px 12px rgba(0,0,0,0.1); }
.spinner-circle-small { width: 20px; height: 20px; border: 2px solid #e2e8f0; border-top: 2px solid #5b2e8e; border-radius: 50%; animation: spin 1s linear infinite; }
.loading-text-small { color: #6b7280; font-size: 14px; font-weight: 500; }
.empty-state { padding: 32px 16px; text-align: center; color: #64748b; }
.empty-illustration-img { width: 220px; height: 220px; max-width: 100%; object-fit: contain; margin-bottom: 12px; }
.pagination-wrapper {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  margin-top: 16px;
}
.pagination-btn {
  min-width: 40px;
  height: 40px;
  padding: 0 14px;
  border: 1px solid #e2e8f0;
  background: white;
  border-radius: 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #64748b;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.2s ease;
}
.pagination-btn:hover:not(:disabled) {
  border-color: #5b2e8e;
  color: #5b2e8e;
}
.pagination-btn:disabled { opacity: 0.4; cursor: not-allowed; background: #f8fafc; }
.pagination-btn.active { background: linear-gradient(135deg, #5b2e8e, #7c3aed); color: white; border-color: #5b2e8e; }
.modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,.5); display: flex; align-items: center; justify-content: center; z-index: 1000; }
.modal { width: min(520px, 92vw); background: #fff; border-radius: 12px; padding: 16px; }
.form-grid { display: grid; grid-template-columns: 110px 1fr; gap: 10px; align-items: center; margin: 12px 0; }
.modal-actions { display: flex; justify-content: flex-end; gap: 8px; }
.toast { position: fixed; right: 16px; top: 84px; padding: 10px 12px; border-radius: 8px; color: #fff; z-index: 1300; }
.toast.success { background: #16a34a; }
.toast.error { background: #dc2626; }
@keyframes spin { to { transform: rotate(360deg); } }
@media (max-width: 768px) { .filters { grid-template-columns: 1fr; } }
</style>
